/* ==========================================================================
   Petit Playa — assets/media-gallery.js
   Add-to-Cart button resilience + Stoq preorder guard
   Last updated: 30 July 2026, by Claude.

   The watchdog block near the BOTTOM of this file keeps the product page's
   Add-to-Cart button correct when the theme's variant-change section fetch is
   slow / blocked / dropped. It re-enables genuinely-in-stock sizes stranded as
   "Sold out", preserves Stoq "Preorder Now" labels, and (2026-07-30) DEFERS to
   Stoq on market-blocked buttons instead of resurrecting them. Full dated change
   log is in the big comment block below.
   ============================================================================= */
if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          // Petit Playa fix (2026-06-23): only scroll image into view for thumbnail
          // clicks (prepend === false), NOT for variant/size changes (prepend === true).
          if (!prepend && (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked')) {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

/* =========================================================================
   Petit Playa — Buy-button resilience  (added 2026-06-23 by Claude)

   ROOT CAUSE: Craft's variant-change handler disables the Add-to-Cart button on
   every size switch and only re-enables it inside a section-fetch .then() with no
   .catch(); a slow/blocked/dropped fetch strands an IN-STOCK size as a greyed
   "Sold out". This watchdog re-enables such stranded-but-available buttons.

   2026-06-28: also correct a mislabelled "Out of stock" (3rd-party app) and stop
   bailing when the button is enabled-but-mislabelled; run via MutationObserver +
   microtask so the fix lands sub-1s and works in background tabs.

   2026-06-28 PREORDER GUARD / 2026-06-29 RE-ENABLE FIX: an active Stoq preorder
   variant must keep Stoq's "Preorder Now: Ships..." label (never relabelled to
   "Add To Cart"), but a stranded [disabled] on such a button IS cleared so it
   stays clickable on a dropped fetch. Active preorder is detected from Stoq's own
   per-variant markers (form `stoq-hide-buy-now` class + a visible preorder desc).

   2026-07-30 MARKET-BLOCK GUARD (bug reported by Emily, confirmed by Stoq):
   Stoq runs separate AU/ROW and US preorder offers. When a variant is in one
   market's offer but sold out in the other market (which has NO offer for it),
   Stoq deliberately DISABLES the button there and shows a market-blocked
   "Sold out" (overriding Shopify continue-selling on the front end; the real
   /cart/add is also blocked). BUT because the variant is continue-selling, the
   product-page JSON reports available:true for EVERY market, so this watchdog's
   `available` gate passed, `stoqPreorderActive` was false, and it cleared Stoq's
   [disabled] + relabelled the button to "Add To Cart" — resurrecting a button
   Stoq had correctly killed (e.g. Maya Dress Hydrangea 2-3Y/5-6Y/6-7Y in the US).
   Fix: Stoq tags every button it controls with `restock-rocket-preorder-button`.
   If that class is present but Stoq is NOT showing an active preorder for this
   market, defer to Stoq entirely (no re-enable, no relabel). Active-preorder and
   normal non-Stoq in-stock buttons are unaffected.

   Touches only [disabled] + the label, never [aria-disabled]/checkout.
   ========================================================================= */
(function () {
  function selectedVariant(vs) {
    const json = vs.querySelector('script[type="application/json"]');
    if (!json) return null;
    let data;
    try {
      data = JSON.parse(json.textContent);
    } catch (e) {
      return null;
    }
    if (!Array.isArray(data)) return null;
    const opts = [];
    const fieldsets = vs.querySelectorAll('fieldset');
    if (fieldsets.length) {
      fieldsets.forEach((fs) => {
        const checked = fs.querySelector('input:checked');
        if (checked) opts.push(checked.value);
      });
    } else {
      vs.querySelectorAll('select').forEach((s) => opts.push(s.value));
    }
    if (!opts.length) return null;
    return (
      data.find(
        (v) => v.options && v.options.length === opts.length && v.options.every((o, i) => o === opts[i])
      ) || null
    );
  }

  function stoqPreorderActive(form) {
    if (form.classList && form.classList.contains('stoq-hide-buy-now')) return true;
    const scope = form.closest('section, .product, [id^="MainProduct"]') || document;
    const desc = scope.querySelector(
      '.restock-rocket-preorder-description, .preorder-description-text, .restock-rocket-preorder-badge'
    );
    return !!(desc && desc.offsetParent !== null);
  }

  // Stoq tags every Add-to-Cart button it controls (any variant in a preorder
  // offer, in ANY market) with this class. A normal Shopify variant with no Stoq
  // offer never carries it.
  function stoqControlsButton(btn) {
    return !!(btn.classList && btn.classList.contains('restock-rocket-preorder-button'));
  }

  function reviveButtons() {
    document.querySelectorAll('variant-selects, variant-radios').forEach((vs) => {
      const section = vs.dataset.section;
      if (!section) return;
      const form = document.getElementById('product-form-' + section);
      if (!form) return;
      const btn = form.querySelector('[name="add"]');
      if (!btn) return;
      const variant = selectedVariant(vs);
      if (!variant || !variant.available) return; // genuinely sold-out -> leave alone

      const preorder = stoqPreorderActive(form);

      // MARKET-BLOCK GUARD (2026-07-30): if Stoq controls this button but is NOT
      // presenting an active preorder for the shopper's market, Stoq has set the
      // state deliberately (market-blocked "Sold out" for a continue-selling variant
      // that reports available:true in every market). Defer to Stoq: do not clear
      // its [disabled] and do not relabel. Active-preorder buttons (preorder===true)
      // are NOT skipped, so they keep the stranded re-enable + Stoq's label.
      if (stoqControlsButton(btn) && !preorder) return;

      if (btn.hasAttribute('disabled')) btn.removeAttribute('disabled');

      if (preorder) return; // clickable now; leave Stoq's "Preorder Now" label.

      const span = btn.querySelector('span');
      if (!span) return;
      const current = (span.textContent || '').trim();
      const strings = window.variantStrings || {};
      const known = [strings.soldOut, strings.unavailable, ''].filter((x) => x !== undefined);
      const looksSoldOut = known.includes(current) || /^(out of stock|sold ?out|unavailable)$/i.test(current);
      if (looksSoldOut && strings.addToCart) {
        span.textContent = strings.addToCart;
      }
    });
  }

  let scheduled = false;
  function scheduleRevive() {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      reviveButtons();
    });
  }

  function observeForms() {
    document.querySelectorAll('variant-selects, variant-radios').forEach((vs) => {
      const section = vs.dataset.section;
      if (!section) return;
      const form = document.getElementById('product-form-' + section);
      if (!form || form.__ppObserved) return;
      form.__ppObserved = true;
      new MutationObserver(scheduleRevive).observe(form, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled'],
      });
    });
  }

  document.addEventListener('change', (e) => {
    if (e.target && e.target.closest && e.target.closest('variant-selects, variant-radios')) {
      scheduleRevive();
      [150, 400, 800].forEach((t) => setTimeout(reviveButtons, t));
    }
  });

  function init() {
    observeForms();
    reviveButtons();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(init, 300));
  } else {
    setTimeout(init, 300);
  }
  setInterval(() => {
    observeForms();
    reviveButtons();
  }, 800);
})();
